/**
 * Keeps the document's language in sync with the active app language.
 *
 * Why this exists
 * ---------------
 * `index.html` ships `<html lang="en">` and nothing ever changed it, so the
 * document claimed to be English no matter which language the caregiver had
 * chosen. That broke `:lang()` CSS, hyphenation, translation-tool detection,
 * and — the one that actually matters for accessibility — the language a
 * screen reader announces content in. A Spanish page read aloud by an English
 * voice is close to unusable.
 *
 * It also gives the app a single honest source of truth for "what language is
 * this document", which `documentLanguage()` exposes to anything that needs a
 * locale for `Intl` (see `humanDate`).
 *
 * What this does NOT fix — do not re-attempt
 * ------------------------------------------
 * It does not change how `<input type="date">` or `<input type="time">` render.
 * A previous version of this file claimed it did. That claim was wrong, and it
 * shipped before it was checked.
 *
 * Tested directly: a page served with `<html lang="es">` rendered a date input
 * as `09/30/2026` and a time input as `02:00 PM` — byte-identical to an
 * `en-US` control on the same machine. Chromium formats these controls from the
 * BROWSER UI LOCALE and ignores `lang` entirely.
 *
 * So a caregiver whose device is English but whose rettX is Spanish still sees
 * `mm/dd/yyyy`. That is a real defect, it is just not this file's to fix, and
 * setting `lang` will never fix it. The actual fix is an Angular-controlled
 * date control — `MAT_DATE_LOCALE` is already provided in `app.config.ts` and
 * currently unused, because nothing renders a `MatDatepicker`.
 *
 * `Intl` with an explicit locale is unaffected by any of this and does work,
 * which is why the display dates a caregiver reads are correct.
 */

/**
 * The app's language codes are NOT all valid BCP-47, so they cannot be written
 * to `lang` as-is.
 *
 * Six are country codes or historical aliases. `ge` is the worst: it is the
 * country code for Georgia, not the language tag for Georgian (`ka`), so a
 * browser silently ignores it and falls back — the bug would look fixed while
 * doing nothing. Anything absent from this map is already a valid tag and is
 * passed through.
 */
const BCP47_BY_APP_LANG: Readonly<Record<string, string>> = {
  cz: 'cs', // Czech
  dk: 'da', // Danish
  ge: 'ka', // Georgian — NOT the country code `ge`
  gr: 'el', // Greek
  se: 'sv', // Swedish
  ua: 'uk'  // Ukrainian
};

export const ACTIVE_APP_LANGUAGE_ATTRIBUTE = 'data-rettx-language';

function normalizeAppLanguage(appLang: string | null | undefined): string {
  return (appLang ?? '').trim().toLowerCase() || 'en';
}

/** Map an app language code to the BCP-47 tag browsers actually understand. */
export function toBcp47(appLang: string | null | undefined): string {
  const code = normalizeAppLanguage(appLang);
  return BCP47_BY_APP_LANG[code] ?? code;
}

/**
 * Write the active language onto `<html lang>`.
 *
 * Safe to call before the DOM is ready and in non-browser contexts (SSR, unit
 * tests with a stubbed document); it simply does nothing.
 */
export function applyDocumentLanguage(appLang: string | null | undefined): void {
  const root = typeof document === 'undefined' ? null : document.documentElement;
  if (!root) {
    return;
  }
  root.setAttribute('lang', toBcp47(appLang));
  root.setAttribute(ACTIVE_APP_LANGUAGE_ATTRIBUTE, normalizeAppLanguage(appLang));
}

/**
 * The language the document is currently in, as a BCP-47 tag.
 *
 * This is the locale to format dates against in plain functions that have no
 * injector — `toLocaleDateString` and friends. It reads back what
 * `applyDocumentLanguage` wrote, so there is one source of truth and no second
 * subscription to keep in sync. Falls back to `en` outside a browser.
 */
export function documentLanguage(): string {
  const root = typeof document === 'undefined' ? null : document.documentElement;
  return root?.getAttribute('lang') || 'en';
}
